import React, { useEffect, useState } from 'react';
import {
  Card, Button, Input, Select, Table, Tag, Tabs, Typography, Alert,
  Popconfirm, Switch, Checkbox, Empty, Spin, Space, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  TeamOutlined, MailOutlined, SettingOutlined, DeleteOutlined, PlusOutlined,
  EditOutlined, ReadOutlined, UndoOutlined, InfoCircleOutlined,
  UserDeleteOutlined, StopOutlined, CheckCircleOutlined, CloseCircleOutlined,
  HourglassOutlined, AppstoreOutlined, BankOutlined, ColumnWidthOutlined,
  CloudOutlined, ThunderboltOutlined, ApiOutlined, FireOutlined, CalendarOutlined,
  SafetyCertificateOutlined, FolderOpenOutlined, MessageOutlined,
  CrownOutlined, HistoryOutlined, SendOutlined,
} from '@ant-design/icons';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import {
  getOrgMembers, getOrgInvites, createInvite, deleteInvite,
  updateOrganization, updateOrgEnabledModules, getMembershipEvents, INVITE_TTL_DAYS,
} from '../firebase/firestore';
import {
  getOrgTaskLibrary, seedOrgTaskLibrary, createOrgTask,
  updateOrgTask, deleteOrgTask,
} from '../firebase/pmTaskLibrary';
import { UserProfile, PendingInvite, OrgRole, OrgPMTask, MembershipEvent } from '../models/types';
import { PM_CATEGORIES } from '../modules/pmScheduler/pmReferenceData';

type PageTab = 'team' | 'library' | 'settings';

const ROLE_OPTIONS: { value: OrgRole; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
  { value: 'contractor', label: 'Contractor' },
];

// Lower rank = less access. Mirrors ROLE_RANK in functions/src/index.ts, where a downgrade
// revokes the member's sessions.
const ROLE_RANK: Record<OrgRole, number> = { contractor: 0, member: 1, admin: 2 };

const roleLabel = (r?: string) => ROLE_OPTIONS.find(o => o.value === r)?.label ?? r ?? 'Member';

/* Invites written before expiresAt existed fall back to createdAt + the TTL, as acceptInvite does. */
const inviteExpiry = (inv: PendingInvite): number =>
  inv.expiresAt ? Date.parse(inv.expiresAt)
    : inv.createdAt ? Date.parse(inv.createdAt) + INVITE_TTL_DAYS * 24 * 60 * 60 * 1000 : 0;

// One readable line per activity-log entry (see recordEvent in functions/src/index.ts).
function describeEvent(e: MembershipEvent): string {
  const actor = e.actorEmail ?? 'Someone';
  const target = e.targetEmail ?? 'a user';
  switch (e.type) {
    case 'org_created': return `${actor} created the organization`;
    case 'invite_sent': return `${actor} invited ${e.email} as ${roleLabel(e.to)}`;
    case 'member_joined':
      return `${target} joined as ${roleLabel(e.to)}${e.actorUid !== e.targetUid ? ` (invited by ${actor})` : ''}`;
    case 'member_left': return `${target} left for another organization`;
    case 'role_changed':
      return `${actor} changed ${target} from ${roleLabel(e.from)} to ${roleLabel(e.to)}${e.via === 'invite' ? ' by invitation' : ''}`;
    case 'member_disabled': return `${actor} disabled ${target}`;
    case 'member_enabled': return `${actor} re-enabled ${target}`;
    case 'member_removed': return `${actor} removed ${target} from the organization`;
    case 'ownership_transferred': return `${actor} transferred ownership to ${target}`;
    case 'contractors_assigned': {
      const parts = [
        e.added?.length ? `added ${e.added.join(', ')}` : '',
        e.removedCount ? `removed ${e.removedCount}` : '',
      ].filter(Boolean).join(' and ');
      return `${actor} updated contractor access on ${e.projectName || 'a project'}${parts ? ` (${parts})` : ''}`;
    }
    default: return `${actor}: ${e.type}`;
  }
}

const CONFIGURABLE_MODULES: { id: string; label: string; icon: React.ReactNode }[] = [
  { id: 'facility', label: 'Facility Assessment', icon: <BankOutlined /> },
  { id: 'dalt', label: 'Duct Leakage', icon: <ColumnWidthOutlined /> },
  { id: 'ebtron', label: 'Flow Calibration', icon: <CloudOutlined /> },
  { id: 'power', label: 'Power Calculator', icon: <ThunderboltOutlined /> },
  { id: 'powerFactor', label: 'Power Factor Correction', icon: <ApiOutlined /> },
  { id: 'boiler', label: 'Boiler ECM', icon: <FireOutlined /> },
  { id: 'pmScheduler', label: 'PM Scheduler', icon: <CalendarOutlined /> },
  { id: 'compliance', label: 'Compliance', icon: <SafetyCertificateOutlined /> },
  { id: 'documents', label: 'Documents', icon: <FolderOpenOutlined /> },
  { id: 'assistant', label: 'Building Assistant', icon: <MessageOutlined /> },
];

const emptyTaskForm = () => ({
  category: PM_CATEGORIES[0],
  assetType: '',
  taskName: '',
  trade: '',
  frequencyValue: 1,
  frequencyUnit: 'months' as OrgPMTask['frequencyUnit'],
  estimatedHours: 1,
  complianceRequired: false,
  complianceStandard: '',
});

const TeamPage: React.FC = () => {
  const { organization, isAdmin, isContractor, orgId, refreshProfile } = useOrg();
  const { currentUser } = useAuth();
  const { token } = theme.useToken();

  // ── Team tab state ────────────────────────────────────────────────────────
  const [pageTab, setPageTab] = useState<PageTab>('team');
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('member');
  const [inviting, setSending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [editingOrgName, setEditingOrgName] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);
  const [pendingRole, setPendingRole] = useState<{ uid: string; role: OrgRole } | null>(null);
  const [savingRoleUid, setSavingRoleUid] = useState<string | null>(null);
  const [events, setEvents] = useState<MembershipEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const ownerUid = organization?.adminUid ?? null;
  const isOwner = !!currentUser && currentUser.uid === ownerUid;

  // ── Settings tab state ────────────────────────────────────────────────────
  // fix: moved outside component to avoid stale closure / missing useEffect dependency
  const [moduleSettings, setModuleSettings] = useState<Record<string, boolean>>(
    () => Object.fromEntries(CONFIGURABLE_MODULES.map(m => [m.id, true]))
  );
  const [savingModules, setSavingModules] = useState(false);
  const [moduleError, setModuleError] = useState('');
  const [moduleSuccess, setModuleSuccess] = useState('');

  // ── Library tab state ─────────────────────────────────────────────────────
  const [libraryTasks, setLibraryTasks] = useState<OrgPMTask[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState('');
  const [librarySeeding, setLibrarySeeding] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<OrgPMTask | null>(null);
  const [taskForm, setTaskForm] = useState(emptyTaskForm());
  const [taskFormError, setTaskFormError] = useState('');
  const [savingTask, setSavingTask] = useState(false);

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    Promise.all([getOrgMembers(orgId), getOrgInvites(orgId)])
      .then(([m, i]) => { setMembers(m); setInvites(i); })
      .catch(() => setError('Failed to load team data'))
      .finally(() => setLoading(false));
  }, [orgId]);

  // The activity log is admin-only (firestore.rules); reloaded after each change made here.
  const loadEvents = React.useCallback(async () => {
    if (!orgId || !isAdmin) return;
    setEventsLoading(true);
    try { setEvents(await getMembershipEvents(orgId)); }
    catch { /* the log is informational; a failed read leaves the last list in place */ }
    finally { setEventsLoading(false); }
  }, [orgId, isAdmin]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    const enabled = organization?.enabledModules;
    if (enabled) {
      setModuleSettings(Object.fromEntries(CONFIGURABLE_MODULES.map(m => [m.id, enabled.includes(m.id)])));
    } else {
      setModuleSettings(Object.fromEntries(CONFIGURABLE_MODULES.map(m => [m.id, true])));
    }
  }, [organization]);

  const loadLibrary = async () => {
    if (!orgId) return;
    setLibraryLoading(true); setLibraryError('');
    try {
      const tasks = await getOrgTaskLibrary(orgId);
      setLibraryTasks(tasks);
      if (tasks.length === 0) {
        setLibrarySeeding(true);
        await seedOrgTaskLibrary(orgId, []);
        const seeded = await getOrgTaskLibrary(orgId);
        setLibraryTasks(seeded);
        setLibrarySeeding(false);
      }
    } catch { setLibraryError('Failed to load task library'); setLibrarySeeding(false); }
    finally { setLibraryLoading(false); }
  };

  const handleTabChange = (tab: PageTab) => {
    setPageTab(tab);
    if (tab === 'library' && libraryTasks.length === 0 && !libraryLoading) {
      loadLibrary();
    }
  };

  const handleResetDefaults = async () => {
    if (!orgId) return;
    setLibrarySeeding(true);
    try {
      await seedOrgTaskLibrary(orgId, libraryTasks);
      const updated = await getOrgTaskLibrary(orgId);
      setLibraryTasks(updated);
    } catch { setLibraryError('Failed to restore defaults'); }
    finally { setLibrarySeeding(false); }
  };

  const openAddForm = () => {
    setEditingTask(null);
    setTaskForm(emptyTaskForm());
    setTaskFormError('');
    setShowTaskForm(true);
  };

  const openEditForm = (task: OrgPMTask) => {
    setEditingTask(task);
    setTaskForm({
      category: task.category,
      assetType: task.assetType,
      taskName: task.taskName,
      trade: task.trade,
      frequencyValue: task.frequencyValue,
      frequencyUnit: task.frequencyUnit,
      estimatedHours: task.estimatedHours,
      complianceRequired: task.complianceRequired,
      complianceStandard: task.complianceStandard ?? '',
    });
    setTaskFormError('');
    setShowTaskForm(true);
  };

  const handleSaveTask = async () => {
    if (!orgId) return;
    if (!taskForm.taskName.trim()) { setTaskFormError('Task Name is required.'); return; }
    if (!taskForm.assetType.trim()) { setTaskFormError('Asset Type is required.'); return; }
    setTaskFormError('');
    setSavingTask(true);
    try {
      const payload = {
        category: taskForm.category,
        assetType: taskForm.assetType.trim(),
        taskName: taskForm.taskName.trim(),
        trade: taskForm.trade.trim(),
        frequencyValue: taskForm.frequencyValue,
        frequencyUnit: taskForm.frequencyUnit,
        estimatedHours: taskForm.estimatedHours,
        complianceRequired: taskForm.complianceRequired,
        ...(taskForm.complianceStandard.trim() ? { complianceStandard: taskForm.complianceStandard.trim() } : {}),
      };
      if (editingTask) {
        await updateOrgTask(editingTask.id, payload);
      } else {
        await createOrgTask(orgId, payload);
      }
      const updated = await getOrgTaskLibrary(orgId);
      setLibraryTasks(updated);
      setShowTaskForm(false);
      setEditingTask(null);
    } catch { setTaskFormError('Failed to save task.'); }
    finally { setSavingTask(false); }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      await deleteOrgTask(id);
      setLibraryTasks(prev => prev.filter(t => t.id !== id));
    } catch { setLibraryError('Failed to delete task.'); }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !inviteEmail.trim()) return;
    setError(''); setInviteSuccess('');
    const email = inviteEmail.trim().toLowerCase();
    if (members.some(m => (m.email ?? '').toLowerCase() === email)) {
      setError(`${inviteEmail.trim()} is already a member. Change their role in the Members table instead.`);
      return;
    }
    setSending(true);
    try {
      await sendInvite(inviteEmail.trim(), inviteRole);
      setInviteSuccess(`Invite email sent to ${inviteEmail.trim()}. They can sign up or log in to join within ${INVITE_TTL_DAYS} days.`);
      setInviteEmail('');
    } catch (err: any) { setError(err?.message ?? 'Failed to send invite'); }
    finally { setSending(false); }
  };

  /* Write the invite (for auto-join on signup) and send the email. Inviting an address again —
     including Resend — overwrites its one invite and restarts the expiry (see createInvite). */
  const sendInvite = async (email: string, role: OrgRole) => {
    if (!orgId || !currentUser) return;
    await createInvite(email, orgId, role, currentUser.uid);
    const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
    await sendInviteEmail({ email, orgId, role });
    setInvites(await getOrgInvites(orgId));
    loadEvents();
  };

  const handleResendInvite = async (inv: PendingInvite) => {
    setError(''); setInviteSuccess('');
    try {
      await sendInvite(inv.email, inv.role);
      setInviteSuccess(`Invite re-sent to ${inv.email}.`);
    } catch (err: any) { setError(err?.message ?? 'Failed to resend invite'); }
  };

  const handleTransferOwnership = async (uid: string) => {
    setError('');
    try {
      await httpsCallable(functions, 'transferOwnership')({ targetUid: uid, orgId });
      await refreshProfile({ quiet: true });
      loadEvents();
    } catch (err: any) { setError(err?.message ?? 'Failed to transfer ownership'); }
  };

  const handleRevokeInvite = async (id: string) => {
    try { await deleteInvite(id); setInvites(prev => prev.filter(i => i.id !== id)); }
    catch { setError('Failed to revoke invite'); }
  };

  // The callables' HttpsError messages (last admin, not in your org) are written for users.
  const handleSetRole = async (uid: string, role: OrgRole) => {
    setPendingRole(null); setSavingRoleUid(uid); setError('');
    try {
      const setMemberRole = httpsCallable(functions, 'setMemberRole');
      await setMemberRole({ targetUid: uid, orgId, role });
      setMembers(prev => prev.map(m => m.uid === uid ? { ...m, role } : m));
      loadEvents();
    } catch (err: any) { setError(err?.message ?? 'Failed to change role'); }
    finally { setSavingRoleUid(null); }
  };

  const handleRemoveMember = async (uid: string) => {
    if (uid === currentUser?.uid) { setError("You can't remove yourself."); return; }
    try {
      const removeFromOrg = httpsCallable(functions, 'removeUserFromOrg');
      await removeFromOrg({ targetUid: uid, orgId });
      setMembers(prev => prev.filter(m => m.uid !== uid));
      loadEvents();
    } catch (err: any) { setError(err?.message ?? 'Failed to remove member'); }
  };

  const handleDisableUser = async (uid: string, currentlyDisabled: boolean) => {
    const action = currentlyDisabled ? 'Re-enable' : 'Disable';
    try {
      const fn = httpsCallable(functions, currentlyDisabled ? 'enableUser' : 'disableUser');
      await fn({ targetUid: uid, orgId });
      setMembers(prev => prev.map(m => m.uid === uid ? { ...m, disabled: !currentlyDisabled } : m));
      loadEvents();
    } catch (err: any) { setError(err?.message ?? `Failed to ${action.toLowerCase()} user`); }
  };

  const handleSaveModules = async () => {
    if (!orgId) return;
    setSavingModules(true); setModuleError(''); setModuleSuccess('');
    try {
      const enabled = CONFIGURABLE_MODULES.filter(m => moduleSettings[m.id]).map(m => m.id);
      await updateOrgEnabledModules(orgId, enabled);
      await refreshProfile();
      setModuleSuccess('Module settings saved.');
    } catch { setModuleError('Failed to save module settings.'); }
    finally { setSavingModules(false); }
  };

  const handleSaveOrgName = async () => {
    if (!orgId || !newOrgName.trim()) return;
    setSavingOrg(true);
    try {
      await updateOrganization(orgId, newOrgName.trim());
      await refreshProfile();
      setEditingOrgName(false);
    } catch { setError('Failed to update name'); }
    finally { setSavingOrg(false); }
  };

  if (!orgId) return (
    <Alert
      type="info" showIcon
      message={<span>You're not part of an organization yet. <a href="/setup">Set one up</a>.</span>}
    />
  );

  const libraryByCategory = libraryTasks.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {} as Record<string, OrgPMTask[]>);

  const fieldWrap = (label: string, node: React.ReactNode, span?: boolean) => (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      {node}
    </div>
  );

  const memberColumns: ColumnsType<UserProfile> = [
    { title: 'Email', dataIndex: 'email' },
    { title: 'Name', dataIndex: 'displayName', render: v => v || '—' },
    {
      title: 'Role', dataIndex: 'role', width: 140,
      render: (v: OrgRole | undefined, m: UserProfile) => {
        const current = v ?? 'member';
        const ownerTag = m.uid === ownerUid
          ? <Tag color="purple" icon={<CrownOutlined />}>Owner</Tag> : null;
        /* Admins change other members' roles here; your own role is changed by another admin, and
           the owner's not at all (the server refuses — ownership has to be transferred first). */
        if (!isAdmin || m.uid === currentUser?.uid || m.uid === ownerUid) {
          return (
            <Space size={2} wrap>
              <Tag color={current === 'admin' ? 'green' : current === 'contractor' ? 'gold' : 'default'}>{current}</Tag>
              {ownerTag}
            </Space>
          );
        }
        const pending = pendingRole?.uid === m.uid ? pendingRole.role : null;
        const label = ROLE_OPTIONS.find(o => o.value === pending)?.label ?? '';
        return (
          <Popconfirm
            open={pending !== null}
            title={pending && ROLE_RANK[pending] < ROLE_RANK[current]
              ? `Change role to ${label}? Their access is reduced, and they'll be signed out within the hour.`
              : `Change role to ${label}?`}
            onConfirm={() => pending && handleSetRole(m.uid, pending)}
            onCancel={() => setPendingRole(null)}
            okText="Change role"
          >
            <Select
              size="small" style={{ width: 120 }} value={current} options={ROLE_OPTIONS}
              loading={savingRoleUid === m.uid} disabled={savingRoleUid === m.uid}
              onChange={r => r !== current && setPendingRole({ uid: m.uid, role: r })}
              aria-label={`Role for ${m.email}`}
            />
          </Popconfirm>
        );
      },
    },
    {
      title: 'Status', dataIndex: 'disabled', width: 110,
      render: (d: boolean | undefined) => d ? <Tag color="red">Disabled</Tag> : <Tag color="green">Active</Tag>,
    },
    {
      title: 'Joined', dataIndex: 'joinedAt', width: 120,
      render: v => <Typography.Text type="secondary">{v ? new Date(v).toLocaleDateString() : '—'}</Typography.Text>,
    },
    ...(isAdmin ? [{
      title: 'Actions', key: 'actions', width: 130,
      render: (_: unknown, m: UserProfile) => (
        m.uid !== currentUser?.uid && m.uid !== ownerUid ? (
          <Space size={4}>
            {isOwner && m.role === 'admin' && !m.disabled && (
              <Popconfirm
                title={`Transfer ownership to ${m.email}? You'll stay an admin, but only they will be able to transfer it again or delete the organization.`}
                onConfirm={() => handleTransferOwnership(m.uid)}
                okText="Transfer"
              >
                <Button type="text" size="small" icon={<CrownOutlined />} title="Make owner" aria-label="Make owner" />
              </Popconfirm>
            )}
            <Popconfirm
              title={m.disabled
                ? 'Re-enable this user? They will be able to log in again.'
                : 'Disable this user? They will not be able to log in until re-enabled.'}
              onConfirm={() => handleDisableUser(m.uid, !!m.disabled)}
              okText={m.disabled ? 'Re-enable' : 'Disable'}
            >
              <Button
                type="text" size="small"
                icon={m.disabled ? <CheckCircleOutlined /> : <StopOutlined />}
                title={m.disabled ? 'Re-enable user' : 'Disable user'}
                aria-label={m.disabled ? 'Re-enable user' : 'Disable user'}
              />
            </Popconfirm>
            <Popconfirm
              title="Remove this member from the organization? They will lose org access but their account will remain."
              onConfirm={() => handleRemoveMember(m.uid)}
              okText="Remove" okButtonProps={{ danger: true }}
            >
              <Button type="text" size="small" danger icon={<UserDeleteOutlined />} title="Remove from org" aria-label="Remove from org" />
            </Popconfirm>
          </Space>
        ) : null
      ),
    }] : []),
  ];

  const inviteColumns: ColumnsType<PendingInvite> = [
    { title: 'Email', dataIndex: 'email' },
    { title: 'Role', dataIndex: 'role', width: 110, render: (v: OrgRole) => <Tag>{v}</Tag> },
    {
      title: 'Sent', dataIndex: 'createdAt', width: 110,
      render: v => <Typography.Text type="secondary">{new Date(v).toLocaleDateString()}</Typography.Text>,
    },
    {
      title: 'Expires', key: 'expires', width: 110,
      render: (_, inv) => {
        const exp = inviteExpiry(inv);
        return exp <= Date.now()
          ? <Tag color="red">Expired</Tag>
          : <Typography.Text type="secondary">{new Date(exp).toLocaleDateString()}</Typography.Text>;
      },
    },
    {
      title: '', key: 'actions', width: 90, align: 'right',
      render: (_, inv) => (
        <Space size={4}>
          <Popconfirm title={`Resend this invite? It will be valid for another ${INVITE_TTL_DAYS} days.`} onConfirm={() => handleResendInvite(inv)} okText="Resend">
            <Button type="text" size="small" icon={<SendOutlined />} title="Resend invite" aria-label="Resend invite" />
          </Popconfirm>
          <Popconfirm title="Revoke this invite?" onConfirm={() => handleRevokeInvite(inv.id)} okText="Revoke" okButtonProps={{ danger: true }}>
            <Button type="text" size="small" danger icon={<CloseCircleOutlined />} aria-label="Revoke invite" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const taskColumns: ColumnsType<OrgPMTask> = [
    { title: 'Asset Type', dataIndex: 'assetType', render: v => <Typography.Text type="secondary">{v}</Typography.Text> },
    { title: 'Task Name', dataIndex: 'taskName', render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { title: 'Trade', dataIndex: 'trade', width: 130, render: v => v || '—' },
    { title: 'Frequency', key: 'frequency', width: 110, render: (_, t) => `${t.frequencyValue} ${t.frequencyUnit}` },
    { title: 'Est. Hrs', dataIndex: 'estimatedHours', width: 90, render: v => `${v}h` },
    {
      title: 'Compliance', dataIndex: 'complianceRequired', width: 140,
      render: (req: boolean, t) => req
        ? <Tag color="gold">{t.complianceStandard || 'Required'}</Tag>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    ...(isAdmin ? [{
      title: '', key: 'actions', width: 90, align: 'right' as const,
      render: (_: unknown, t: OrgPMTask) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditForm(t)} title="Edit" aria-label="Edit task" />
          <Popconfirm title="Delete this task from your library?" onConfirm={() => handleDeleteTask(t.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" aria-label="Delete task" />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const tabItems = [
    { key: 'team', label: <span><TeamOutlined /> Manage Members</span> },
    ...(isAdmin ? [
      { key: 'library', label: <span><ReadOutlined /> PM Task Library</span> },
      { key: 'settings', label: <span><SettingOutlined /> Settings</span> },
    ] : []),
  ];

  // Team & organization management is not available to the restricted contractor tier.
  if (isContractor) {
    return (
      <div style={{ maxWidth: 980 }}>
        <Alert
          type="info" showIcon
          message="Not available"
          description="Team & organization management isn't available for contractor accounts."
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980 }}>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <Typography.Title level={4} style={{ margin: 0 }}><TeamOutlined /> Team &amp; Organization</Typography.Title>
        <Typography.Text type="secondary">{organization?.name}</Typography.Text>
      </Card>

      <Tabs
        activeKey={pageTab}
        onChange={k => handleTabChange(k as PageTab)}
        items={tabItems}
      />

      {/* ── TEAM TAB ──────────────────────────────────────────────────────── */}
      {pageTab === 'team' && (
        <>
          {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}

          {/* Org info */}
          <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
            {editingOrgName ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <Input style={{ flex: 1 }} value={newOrgName} onChange={e => setNewOrgName(e.target.value)} autoFocus />
                <Button type="primary" loading={savingOrg} onClick={handleSaveOrgName}>Save</Button>
                <Button onClick={() => setEditingOrgName(false)}>Cancel</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <Typography.Text strong style={{ fontSize: 15 }}>{organization?.name}</Typography.Text>
                  <div><Typography.Text type="secondary" style={{ fontSize: 13 }}>Organization</Typography.Text></div>
                </div>
                {isAdmin && (
                  <Button icon={<EditOutlined />} onClick={() => { setNewOrgName(organization?.name ?? ''); setEditingOrgName(true); }}>
                    Rename
                  </Button>
                )}
              </div>
            )}
          </Card>

          {/* Invite form */}
          {isAdmin && (
            <Card title={<span><MailOutlined /> Invite Member</span>} style={{ marginBottom: 16 }}>
              {inviteSuccess && <Alert type="success" showIcon message={inviteSuccess} style={{ marginBottom: 14 }} />}
              <form onSubmit={handleInvite}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, marginBottom: 6 }}>Email Address</div>
                    <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@company.com" required />
                  </div>
                  <div style={{ width: 130 }}>
                    <div style={{ fontSize: 13, marginBottom: 6 }}>Role</div>
                    <Select
                      style={{ width: '100%' }} value={inviteRole}
                      options={ROLE_OPTIONS}
                      onChange={v => setInviteRole(v as OrgRole)}
                    />
                  </div>
                  <Button type="primary" htmlType="submit" icon={<MailOutlined />} loading={inviting}>Send Invite</Button>
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
                  <InfoCircleOutlined /> The person is automatically joined to your org when they sign up or log in with this email.
                  {inviteRole === 'contractor' && ' Contractors see only the projects you assign to them, and can only fill out facility assessments and view the report — assign projects from each project’s page.'}
                </Typography.Text>
              </form>
            </Card>
          )}

          {/* Members */}
          <Card
            title={<span><TeamOutlined /> Members ({members.length})</span>}
            style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}
          >
            <Table<UserProfile>
              rowKey="uid"
              size="middle"
              loading={loading}
              columns={memberColumns}
              dataSource={members}
              pagination={false}
              onRow={m => (m.disabled ? { style: { opacity: 0.6 } } : {})}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No members yet." /> }}
            />
          </Card>

          {/* Pending invites */}
          {isAdmin && invites.length > 0 && (
            <Card
              title={<span><HourglassOutlined /> Pending Invites ({invites.length})</span>}
              style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}
            >
              <Table<PendingInvite>
                rowKey="id"
                size="middle"
                columns={inviteColumns}
                dataSource={invites}
                pagination={false}
              />
            </Card>
          )}

          {/* Activity — who changed whose access, and when (admins only) */}
          {isAdmin && (
            <Card
              title={<span><HistoryOutlined /> Activity</span>}
              style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}
            >
              <Table<MembershipEvent>
                rowKey="id"
                size="small"
                loading={eventsLoading}
                dataSource={events}
                pagination={events.length > 10 ? { pageSize: 10, size: 'small' } : false}
                columns={[
                  {
                    title: 'When', dataIndex: 'at', width: 170,
                    render: v => <Typography.Text type="secondary">{v ? new Date(v).toLocaleString() : '—'}</Typography.Text>,
                  },
                  { title: 'Activity', key: 'what', render: (_, e) => describeEvent(e) },
                ]}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity recorded yet." /> }}
              />
            </Card>
          )}
        </>
      )}

      {/* ── PM TASK LIBRARY TAB ───────────────────────────────────────────── */}
      {pageTab === 'library' && isAdmin && (
        <>
          {libraryError && <Alert type="error" showIcon message={libraryError} closable onClose={() => setLibraryError('')} style={{ marginBottom: 16 }} />}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <div>
              <Typography.Text strong style={{ fontSize: 15 }}>PM Task Library</Typography.Text>
              <div><Typography.Text type="secondary" style={{ fontSize: 13 }}>{libraryTasks.length} tasks · shown in the PM Scheduler task dropdown</Typography.Text></div>
            </div>
            <Space>
              <Popconfirm
                title="This will restore any deleted default tasks. Your customizations will not be affected. Continue?"
                onConfirm={handleResetDefaults}
                okText="Restore"
              >
                <Button icon={<UndoOutlined />} loading={librarySeeding}>Restore Defaults</Button>
              </Popconfirm>
              <Button type="primary" icon={<PlusOutlined />} onClick={openAddForm}>Add Task</Button>
            </Space>
          </div>

          {/* Add / Edit form */}
          {showTaskForm && (
            <Card title={editingTask ? 'Edit task' : 'New task'} style={{ marginBottom: 16 }}>
              <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                {fieldWrap('Category *', (
                  <Select
                    style={{ width: '100%' }} value={taskForm.category}
                    options={PM_CATEGORIES.map(c => ({ value: c, label: c }))}
                    onChange={v => setTaskForm(f => ({ ...f, category: v }))}
                  />
                ))}
                {fieldWrap('Asset Type *', (
                  <Input value={taskForm.assetType} onChange={e => setTaskForm(f => ({ ...f, assetType: e.target.value }))} placeholder="e.g. Air Handling Unit" />
                ))}
                {fieldWrap('Task Name *', (
                  <Input value={taskForm.taskName} onChange={e => setTaskForm(f => ({ ...f, taskName: e.target.value }))} placeholder="e.g. Air Filter Replacement" />
                ))}
                {fieldWrap('Trade', (
                  <Input value={taskForm.trade} onChange={e => setTaskForm(f => ({ ...f, trade: e.target.value }))} placeholder="e.g. HVAC Tech" />
                ))}
                {fieldWrap('Frequency', (
                  <Input type="number" min={1} value={taskForm.frequencyValue} onChange={e => setTaskForm(f => ({ ...f, frequencyValue: parseInt(e.target.value) || 1 }))} />
                ))}
                {fieldWrap('Unit', (
                  <Select
                    style={{ width: '100%' }} value={taskForm.frequencyUnit}
                    options={[
                      { value: 'days', label: 'Days' }, { value: 'weeks', label: 'Weeks' },
                      { value: 'months', label: 'Months' }, { value: 'years', label: 'Years' },
                    ]}
                    onChange={v => setTaskForm(f => ({ ...f, frequencyUnit: v as OrgPMTask['frequencyUnit'] }))}
                  />
                ))}
                {fieldWrap('Est. Hours', (
                  <Input type="number" min={0} step={0.5} value={taskForm.estimatedHours} onChange={e => setTaskForm(f => ({ ...f, estimatedHours: parseFloat(e.target.value) || 0 }))} />
                ))}
                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 5 }}>
                  <Checkbox
                    checked={taskForm.complianceRequired}
                    onChange={e => setTaskForm(f => ({ ...f, complianceRequired: e.target.checked }))}
                  >
                    Compliance Required
                  </Checkbox>
                </div>
                {taskForm.complianceRequired && fieldWrap('Compliance Standard', (
                  <Input value={taskForm.complianceStandard} onChange={e => setTaskForm(f => ({ ...f, complianceStandard: e.target.value }))} placeholder="e.g. NFPA 25, ASHRAE 188" />
                ), true)}
              </div>
              {taskFormError && <Alert type="error" showIcon message={taskFormError} style={{ marginTop: 14 }} />}
              <Space style={{ marginTop: 16 }}>
                <Button type="primary" loading={savingTask} onClick={handleSaveTask}>Save</Button>
                <Button onClick={() => { setShowTaskForm(false); setEditingTask(null); setTaskFormError(''); }}>Cancel</Button>
              </Space>
            </Card>
          )}

          {/* Task list */}
          {libraryLoading || librarySeeding ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <Spin />
              <div style={{ marginTop: 12 }}>
                <Typography.Text type="secondary">{librarySeeding ? 'Setting up your task library…' : 'Loading…'}</Typography.Text>
              </div>
            </div>
          ) : libraryTasks.length === 0 ? (
            <Card>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span>No tasks yet. Click <strong>Add Task</strong> to get started.</span>}
              />
            </Card>
          ) : (
            Object.entries(libraryByCategory).sort(([a], [b]) => a.localeCompare(b)).map(([cat, tasks]) => (
              <Card
                key={cat}
                title={cat}
                extra={<Typography.Text type="secondary" style={{ fontSize: 13 }}>{tasks.length} tasks</Typography.Text>}
                style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}
              >
                <Table<OrgPMTask>
                  rowKey="id"
                  size="middle"
                  columns={taskColumns}
                  dataSource={tasks}
                  pagination={false}
                />
              </Card>
            ))
          )}
        </>
      )}

      {/* ── SETTINGS TAB ─────────────────────────────────────────────────── */}
      {pageTab === 'settings' && isAdmin && (
        <Card title={<span><AppstoreOutlined /> Visible Modules</span>}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Control which modules are visible to your team in the project hub. Disabled modules are hidden from all members.
          </Typography.Paragraph>
          {moduleError && <Alert type="error" showIcon message={moduleError} style={{ marginBottom: 14 }} />}
          {moduleSuccess && <Alert type="success" showIcon message={moduleSuccess} style={{ marginBottom: 14 }} />}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 18 }}>
            {CONFIGURABLE_MODULES.map(m => {
              const on = moduleSettings[m.id];
              return (
                <div
                  key={m.id}
                  onClick={() => setModuleSettings(s => ({ ...s, [m.id]: !s[m.id] }))}
                  style={{
                    cursor: 'pointer',
                    border: `1px solid ${on ? token.colorPrimaryBorder : token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                    padding: '14px 16px',
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: on ? token.colorPrimaryBg : token.colorBgContainer,
                    opacity: on ? 1 : 0.6,
                  }}
                >
                  <span style={{ fontSize: 20, color: on ? token.colorPrimary : token.colorTextTertiary }}>{m.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{m.label}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{on ? 'Visible' : 'Hidden'}</Typography.Text>
                  </div>
                  <Switch
                    checked={on}
                    onChange={() => setModuleSettings(s => ({ ...s, [m.id]: !s[m.id] }))}
                    onClick={(_, e) => e.stopPropagation()}
                  />
                </div>
              );
            })}
          </div>
          <Button type="primary" loading={savingModules} onClick={handleSaveModules}>Save Settings</Button>
        </Card>
      )}
    </div>
  );
};

export default TeamPage;
